import type { FinancialSummaryYear } from './financial-summary';

// Corpuls redizajn (2026-07-15) — rozšírené finančné ukazovatele, FinStat-
// inšpirované (viď /root/.claude/plans/abstract-finding-unicorn.md). Čisté
// výpočtové funkcie — žiadne I/O, žiadny stav. Delenie nulou/null vždy vracia
// `null` (nikdy NaN/Infinity), rovnaký princíp ako appka doteraz — radšej
// chýbajúce číslo než zavádzajúce.
function ratio(numerator: number | null, denominator: number | null): number | null {
  if (numerator == null || denominator == null || denominator === 0) return null;
  return numerator / denominator;
}

function days(numerator: number | null, denominator: number | null): number | null {
  const r = ratio(numerator, denominator);
  return r == null ? null : r * 365;
}

export type RatioUnit = 'pct' | 'ratio' | 'days';

export interface RatioRow {
  key: string;
  label: string;
  value: number | null;
  unit: RatioUnit;
  /** Voliteľná poznámka o obmedzenej platnosti (napr. hrubá marža). */
  hint?: string;
}

export interface RatioCategory {
  key: string;
  label: string;
  rows: RatioRow[];
}

/**
 * Vypočíta všetky ukazovatele pre jeden rok, voliteľne s predchádzajúcim rokom
 * pre trend (zatiaľ nepoužité v `RatioRow` samotnom — trend počíta volajúci
 * kód porovnaním `value` medzi dvoma volaniami tejto funkcie, viď CompanyPage).
 *
 * POZOR — "Hrubá marža (obchodná)": `cogs` je úzko definované ako "náklady
 * vynaložené na obstaranie predaného tovaru" (RÚZ Výkaz ziskov a strát, "A."),
 * teda len tovar na ďalší predaj — NIE plnohodnotné COGS pre výrobné/servisné
 * firmy. Zmysluplné len pre firmy s tržbami z predaja tovaru; u ostatných
 * (napr. IT služby) vyjde marža blízka 100 % alebo null, čo NEZNAMENÁ, že
 * firma nemá náklady — len že tento konkrétny riadok pre ňu meria niečo iné.
 *
 * Oprava 2026-07-24 — "Dlh/EBITDA" predtým aproximovalo úročený dlh CELKOVÝMI
 * záväzkami (appka vtedy nemala oddelené bankové úvery dekódované), preto
 * bolo systematicky vyššie číslo než FinStat-štýl "Dlh/EBITDA". Teraz používa
 * `bankLoansTotal` (dlhodobé + bežné bankové úvery + krátkodobé finančné
 * výpomoci, RuzTemplateClient) — skutočný úročený dlh, nie aproximáciu.
 */
export function computeFinancialRatios(year: FinancialSummaryYear): RatioCategory[] {
  const {
    totalAssets,
    equity,
    liabilities,
    netIncome,
    revenue,
    ebit,
    operatingResult,
    ebitda,
    workingCapital,
    currentAssets,
    inventory,
    shortTermReceivables,
    financialAssets,
    shortTermLiabilities,
    tradePayables,
    cogs,
    costOfRevenue,
    interestExpense,
    retainedEarnings,
    bankLoansTotal,
    personnelCosts,
    wageCosts,
  } = year;

  return [
    {
      key: 'marze',
      label: 'Marže',
      rows: [
        {
          key: 'hruba-marza',
          label: 'Hrubá marža (obchodná)',
          value: revenue != null && cogs != null ? ratio(revenue - cogs, revenue) : null,
          unit: 'pct',
          hint: 'Zmysluplné len pre firmy s tržbami z predaja tovaru.',
        },
        { key: 'ebitda-marza', label: 'EBITDA marža', value: ratio(ebitda, revenue), unit: 'pct' },
        {
          key: 'prevadzkova-marza',
          label: 'Prevádzková marža',
          value: ratio(operatingResult, revenue),
          unit: 'pct',
        },
        {
          key: 'cista-ziskova-marza',
          label: 'Čistá zisková marža',
          value: ratio(netIncome, revenue),
          unit: 'pct',
        },
      ],
    },
    {
      key: 'zadlzenost',
      label: 'Ukazovatele zadlženosti',
      rows: [
        {
          key: 'celkova-zadlzenost',
          label: 'Celková zadlženosť',
          value: ratio(liabilities, totalAssets),
          unit: 'pct',
        },
        {
          key: 'zadlzenost-vi',
          label: 'Zadlženosť vlastného imania (D/E)',
          value: ratio(liabilities, equity),
          unit: 'ratio',
        },
        {
          key: 'kratkodoba-zadlzenost',
          label: 'Krátkodobá zadlženosť',
          value: ratio(shortTermLiabilities, totalAssets),
          unit: 'pct',
        },
        {
          key: 'samofinancovanie',
          label: 'Miera samofinancovania',
          value: ratio(equity, totalAssets),
          unit: 'pct',
        },
        {
          key: 'dlh-ebitda',
          label: 'Bankové úvery / EBITDA',
          value: ratio(bankLoansTotal, ebitda),
          unit: 'ratio',
          hint: 'Skutočný úročený dlh (bankové úvery + finančné výpomoci), nie celkové záväzky.',
        },
        {
          key: 'urokove-krytie',
          label: 'Úrokové krytie',
          value: ratio(ebit, interestExpense),
          unit: 'ratio',
        },
      ],
    },
    {
      key: 'likvidita',
      label: 'Ukazovatele likvidity',
      rows: [
        {
          key: 'bezna-likvidita',
          label: 'Bežná likvidita (3. stupňa)',
          value: ratio(currentAssets, shortTermLiabilities),
          unit: 'ratio',
        },
        {
          key: 'rychla-likvidita',
          label: 'Rýchla likvidita (2. stupňa)',
          value:
            currentAssets != null && inventory != null
              ? ratio(currentAssets - inventory, shortTermLiabilities)
              : null,
          unit: 'ratio',
        },
        {
          key: 'hotovostna-likvidita',
          label: 'Hotovostná likvidita (1. stupňa)',
          value: ratio(financialAssets, shortTermLiabilities),
          unit: 'ratio',
        },
      ],
    },
    {
      key: 'rentabilita',
      label: 'Ukazovatele rentability',
      rows: [
        {
          key: 'roa',
          label: 'Návratnosť aktív (ROA)',
          value: ratio(netIncome, totalAssets),
          unit: 'pct',
        },
        {
          key: 'roe',
          label: 'Návratnosť vlastného imania (ROE)',
          value: ratio(netIncome, equity),
          unit: 'pct',
        },
      ],
    },
    {
      key: 'aktivita',
      label: 'Ukazovatele aktivity',
      rows: [
        {
          key: 'obrat-aktiv',
          label: 'Obrat aktív',
          value: ratio(revenue, totalAssets),
          unit: 'ratio',
        },
        {
          key: 'obrat-zasob',
          label: 'Obrat zásob',
          value: ratio(costOfRevenue, inventory),
          unit: 'ratio',
        },
        {
          key: 'doba-obratu-zasob',
          label: 'Doba obratu zásob',
          value: days(inventory, costOfRevenue),
          unit: 'days',
        },
        {
          key: 'doba-inkasa-pohladavok',
          label: 'Doba inkasa pohľadávok (DSO)',
          value: days(shortTermReceivables, revenue),
          unit: 'days',
        },
        {
          key: 'doba-splacania-zavazkov',
          label: 'Doba splácania záväzkov (DPO)',
          value: days(tradePayables, costOfRevenue),
          unit: 'days',
        },
      ],
    },
    {
      key: 'naklady',
      label: 'Nákladová štruktúra',
      rows: [
        {
          key: 'osobne-naklady-trzby',
          label: 'Osobné náklady / tržby',
          value: ratio(personnelCosts, revenue),
          unit: 'pct',
          hint: 'Mzdy, odvody aj odmeny orgánov spolu — proxy nákladovosti práce (appka nemá počet zamestnancov).',
        },
        {
          key: 'mzdy-trzby',
          label: 'Mzdové náklady / tržby',
          value: ratio(wageCosts, revenue),
          unit: 'pct',
          hint: 'Len samotné mzdy, bez odvodov a odmien orgánov. Dostupné len pre časť firiem — RÚZ šablóna, ktorú firma použila, musí osobné náklady rozpisovať (nie každá to robí).',
        },
      ],
    },
    {
      key: 'kreditne-modely',
      label: 'Kreditné modely',
      rows: [
        {
          key: 'altman-x1',
          label: 'Altman X1 (pracovný kapitál/aktíva)',
          value: ratio(workingCapital, totalAssets),
          unit: 'ratio',
        },
        {
          key: 'altman-x2',
          label: 'Altman X2 (nerozdelený zisk/aktíva)',
          value: ratio(retainedEarnings, totalAssets),
          unit: 'ratio',
        },
        {
          key: 'altman-x3',
          label: 'Altman X3 (EBIT/aktíva)',
          value: ratio(ebit, totalAssets),
          unit: 'ratio',
        },
        {
          key: 'altman-x4',
          label: 'Altman X4 (vlastné imanie/záväzky)',
          value: ratio(equity, liabilities),
          unit: 'ratio',
        },
        {
          key: 'altman-x5',
          label: 'Altman X5 (tržby/aktíva)',
          value: ratio(revenue, totalAssets),
          unit: 'ratio',
        },
      ],
    },
  ];
}
